import { createBrowserClient as createBrowserClientSSR } from "@supabase/ssr"
import type { SupabaseClient } from "@supabase/supabase-js"

let supabaseInstance: SupabaseClient | null = null
let isWarmedUp = false

// Warm up the connection by making a lightweight query
async function warmUpConnection(client: SupabaseClient) {
  if (isWarmedUp) return
  
  try {
    console.log("[Supabase] Warming up connection...")
    const start = Date.now()
    // Simple query to wake up the connection
    await client.from("profiles").select("id").limit(1).maybeSingle()
    const time = Date.now() - start
    console.log(`[Supabase] Connection warmed up in ${time}ms`)
    isWarmedUp = true
  } catch (error) {
    console.warn("[Supabase] Connection warmup failed (non-critical):", error)
  }
}

export function createClient() {
  if (supabaseInstance) {
    return supabaseInstance
  }

  const url = process.env.NEXT_PUBLIC_SUPABASE_URL
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY

  if (!url || !anonKey) {
    console.error("[Supabase] Missing environment variables", {
      url: url ? "set" : "missing",
      anonKey: anonKey ? "set" : "missing",
    })
    return {
      auth: {
        getSession: async () => ({ data: { session: null }, error: null }),
        getUser: async () => ({ data: { user: null }, error: null }),
        onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
        signOut: async () => ({ error: null }),
      },
      from: () => ({
        select: () => ({
          eq: () => ({
            single: async () => ({ data: null, error: null }),
            maybeSingle: async () => ({ data: null, error: null }),
          }),
        }),
      }),
    } as any
  }

  supabaseInstance = createBrowserClientSSR(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: "pkce",
      storageKey: "goonru-auth", // Unique key to prevent session sharing with other apps
    },
    global: {
      headers: {
        'x-client-info': 'goonru-web',
      },
    },
  })

  // Warm up connection in background (non-blocking)
  warmUpConnection(supabaseInstance).catch(() => {})

  return supabaseInstance
}

// Call this on sign out so the next createClient() gets a fresh instance
export function resetClient() {
  supabaseInstance = null
  isWarmedUp = false
}

export const createBrowserClient = createClient